// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Button, GridContainer, GridItem, Link, LoadingOverlay, Select, TextField } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rc/App/Components/Notify";
import activeUpdateCustomKit from "@rc/App/Handlers/GeneticKit/activeUpdateCustomKit";
import getCustomerKitById from "@rc/App/Handlers/GeneticKit/getCustomerKitById";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { PetModel, PetOwnerModel, VetModel } from "@rc/App/Widgets/GeneticKit/GeneticKit";
import { xValidateOnSubmit } from "@rc/XDL/Utilities";
import * as cssLinter from "css";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const formId = "activateGeneticKitForm";

interface CustomerKitModel {
    ClinicId: string;
    KitId: string;
    Pet: PetModel;
    PetOwner: PetOwnerModel;
    Vet: VetModel;
}

export interface GenticKitsActivateObjectModel {
    ClinicId: string;
    KitId: string;
    Salutation: string;
    VetFname: string;
    VetLname: string;
    VetEmail: string;
    ConfirmVetEmail: string;
    OwnerFname: string;
    OwnerLname: string;
    OwnerPhone: string;
    OwnerEmail: string;
    ConfirmOwnerEmail: string;
    PetName: string;
    PetBirthDate: string;
    PetGender: string;
    SpayedOrNeutered: string;
}

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);
    const shipToState = getShipToState(state, state.context.session.shipToId);

    let kitIdParams;
    if (location?.search) {
        kitIdParams = parseQueryString<{ KitId: string }>(location.search);
    }

    let shipTo;
    if (shipToState && shipToState.value) {
        shipTo = shipToState.value;
    }

    return {
        geneticKitManagementUrl: getPageLinkByPageType(state, "GeneticKitManagement")?.url,
        kitIdParams,
        shipTo,
    };
};

const mapDispatchToProps = {
    getCustomerKitById,
    activeUpdateCustomKit,
};

export interface OwnProps extends WidgetProps {}

type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const enum fields {}

export interface ActivateGeneticKitStyles {
    gridItem: GridItemProps;
    actionsGridItem: GridItemProps;
    petBirthDate?: DatePickerPresentationProps;

    errorMessage: InjectableCss;
}

export const activateGeneticKitStyles: ActivateGeneticKitStyles = {
    gridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            align-items: flex-end;
            > div {
                width: 100%;
            }
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            justify-content: flex-end;
            align-items: flex-end;
            button {
                margin-left: 30px;
            }
        `,
    },
    petBirthDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    errorMessage: {
        css: css`
            margin-top: 25px;
        `,
    },
};

const styles = activateGeneticKitStyles;

const ActivateGeneticKit: React.FunctionComponent<Props> = (props: Props) => {
    const { geneticKitManagementUrl, kitIdParams, shipTo, history, getCustomerKitById, activeUpdateCustomKit } = props;

    const [genticKitsActivateInReview, setGenticKitsActivateInReview] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormDisabled, setIsFormDisabled] = useState(false);
    const [ClinicId, setClinicId] = useState("");
    const [KitId, setKitId] = useState("");
    const [Salutation, setSalutation] = useState("dr");
    const [VetFname, setVetFname] = useState("");
    const [VetLname, setVetLname] = useState("");
    const [VetEmail, setVetEmail] = useState("");
    const [ConfirmVetEmail, setConfirmVetEmail] = useState("");
    const [OwnerFname, setOwnerFname] = useState("");
    const [OwnerLname, setOwnerLname] = useState("");
    const [OwnerPhone, setOwnerPhone] = useState("");
    const [OwnerEmail, setOwnerEmail] = useState("");
    const [ConfirmOwnerEmail, setConfirmOwnerEmail] = useState("");
    const [PetName, setPetName] = useState("");
    const [PetBirthDate, setPetBirthDate] = useState(convertDateToDateOnlyString(new Date()));
    const [PetGender, setPetGender] = useState("Male");
    const [SpayedOrNeutered, setSpayedOrNeutered] = useState("True");

    const [errorMessage, setErrorMessage] = useState("");

    const toasterContext = useContext(ToasterContext);

    useEffect(() => {
        if (kitIdParams) {
            setKitId(kitIdParams.KitId);
            setIsLoading(true);
            getCustomerKitById({
                kitId: kitIdParams.KitId,
                onSuccess: response => {
                    setIsLoading(false);
                    fillForm(response?.CustomerKit);
                },
                onError: error => {
                    setIsLoading(false);
                },
            });
        }
    }, []);

    useEffect(() => {
        setClinicId(shipTo?.customerSequence || "");
    }, [shipTo]);

    const fillForm = (CustomerKit: CustomerKitModel) => {
        setClinicId(CustomerKit.ClinicId);
        setKitId(CustomerKit.KitId);
        setSalutation(CustomerKit.Vet.Salutation);
        setVetFname(CustomerKit.Vet.FirstName);
        setVetLname(CustomerKit.Vet.LastName);
        setVetEmail(CustomerKit.Vet.Email);
        setConfirmVetEmail(CustomerKit.Vet.Email);
        setOwnerFname(CustomerKit.PetOwner.FirstName);
        setOwnerLname(CustomerKit.PetOwner.LastName);
        setOwnerPhone(CustomerKit.PetOwner.Phone);
        setOwnerEmail(CustomerKit.PetOwner.Email);
        setConfirmOwnerEmail(CustomerKit.PetOwner.Email);
        setPetName(CustomerKit.Pet.Name);
        setPetBirthDate(CustomerKit.Pet.DateOfBirth);
        setPetGender(CustomerKit.Pet.Gender);
        setSpayedOrNeutered(CustomerKit.Pet.SpayedOrNeutered);
    };

    const formIsValid = () => {
        const requiredFields = [
            ClinicId,
            KitId,
            VetFname,
            VetLname,
            VetEmail,
            ConfirmVetEmail,
            OwnerFname,
            OwnerLname,
            OwnerPhone,
            OwnerEmail,
            ConfirmOwnerEmail,
            PetName,
        ];

        return requiredFields.every(field => field && field.trim() !== "");
    };

    const handleSubmit = (event: React.MouseEvent<HTMLFormElement, MouseEvent>) => {
        event.preventDefault();
        const errors = xValidateOnSubmit(formId);

        if (!formIsValid() || errors.length > 0) {
            setIsLoading(false);
            return;
        }

        if (!genticKitsActivateInReview) {
            setGenticKitsActivateInReview(true);
            setIsFormDisabled(true);
            return;
        }

        const genticKitsActivateObject = {
            ClinicId,
            KitId,
            Salutation,
            VetFname,
            VetLname,
            VetEmail,
            ConfirmVetEmail,
            OwnerFname,
            OwnerLname,
            OwnerPhone,
            OwnerEmail,
            ConfirmOwnerEmail,
            PetName,
            PetBirthDate,
            PetGender,
            SpayedOrNeutered,
        } as GenticKitsActivateObjectModel;

        setIsSubmitting(true);

        activeUpdateCustomKit({
            isUpdate: !!kitIdParams?.KitId,
            paramenter: genticKitsActivateObject,
            onSuccess: response => {
                setIsSubmitting(false);
                toasterContext.addToast({
                    body: siteMessage("RC_Successful_ActiveKit"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
            },
            onError: error => {
                setErrorMessage(error?.errorJson?.message || error?.errorJson?.description || error?.description);
                setIsSubmitting(false);
            },
        });
    };

    const cancelAndBack = () => {
        if (genticKitsActivateInReview) {
            setGenticKitsActivateInReview(false);
            setIsFormDisabled(false);
            return;
        }

        history.push(geneticKitManagementUrl || "/MyAccount/genetickits");
    };

    const regex = /[!@#$%^&*()_+{}\'\"\[\]:;<>=,.?`~\\/-]/g;

    return (
        <LoadingOverlay loading={isLoading || isSubmitting}>
            <StyledWrapper>
                <form id="geneticKitForm" onSubmit={handleSubmit} noValidate={true}>
                    <GridContainer>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Clinic Id")}
                                value={ClinicId}
                                onChange={event => setClinicId(event.target.value)}
                                data-test-selector="ClinicId"
                                type="text"
                                disabled
                                required
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Kit Id")}
                                value={KitId}
                                onChange={event => setKitId(event.target.value.replace(regex, ""))}
                                data-test-selector="KitId"
                                type="text"
                                required
                                disabled={!!kitIdParams || isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                label={translate("Salutation")}
                                onChange={event => setSalutation(event.target.value)}
                                value={Salutation}
                                disabled={isFormDisabled}
                            >
                                <option value="dr">{translate("Dr.")}</option>
                                <option value="mrs">{translate("Mrs.")}</option>
                                <option value="mr">{translate("Mr.")}</option>
                                <option value="ms">{translate("Ms.")}</option>
                                <option value="prof">{translate("Prof.")}</option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Vet First Name")}
                                value={VetFname}
                                onChange={event => setVetFname(event.target.value)}
                                data-test-selector="VetFname"
                                type="text"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Vet Last Name")}
                                value={VetLname}
                                onChange={event => setVetLname(event.target.value)}
                                data-test-selector="VetLname"
                                type="text"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Vet Email")}
                                value={VetEmail}
                                onChange={event => setVetEmail(event.target.value)}
                                data-test-selector="VetEmail"
                                type="email"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Confirm Vet Email")}
                                value={ConfirmVetEmail}
                                onChange={event => setConfirmVetEmail(event.target.value)}
                                data-test-selector="ConfirmVetEmail"
                                type="email"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Owner First Name")}
                                value={OwnerFname}
                                onChange={event => setOwnerFname(event.target.value)}
                                data-test-selector="OwnerFname"
                                type="text"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Owner Last Name")}
                                value={OwnerLname}
                                onChange={event => setOwnerLname(event.target.value)}
                                data-test-selector="OwnerLname"
                                type="text"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Owner Phone Number")}
                                value={OwnerPhone}
                                onChange={event => setOwnerPhone(event.target.value)}
                                data-test-selector="OwnerPhone"
                                type="phone"
                                required
                                disabled={isFormDisabled}
                                maxLength={15}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Owner Email")}
                                value={OwnerEmail}
                                onChange={event => setOwnerEmail(event.target.value)}
                                data-test-selector="OwnerEmail"
                                type="email"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Confirm Owner Email")}
                                value={ConfirmOwnerEmail}
                                onChange={event => setConfirmOwnerEmail(event.target.value)}
                                data-test-selector="ConfirmOwnerEmail"
                                type="email"
                                required
                                disabled={isFormDisabled}
                                maxLength={75}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={translate("Pet Name")}
                                value={PetName}
                                onChange={event => setPetName(event.target.value)}
                                data-test-selector="PetName"
                                type="text"
                                required
                                disabled={isFormDisabled}
                                maxLength={50}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <DatePicker
                                {...styles.petBirthDate}
                                label={translate("Pet Birthdate")}
                                selectedDay={PetBirthDate ? new Date(PetBirthDate.replace(/-/g, "/")) : new Date()}
                                disabled={isFormDisabled}
                                onDayChange={({ selectedDay }: Pick<DatePickerState, "selectedDay">) =>
                                    setPetBirthDate(convertDateToDateOnlyString(selectedDay))
                                }
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    maxDate: new Date(),
                                }}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                label={translate("Pet Gender")}
                                onChange={event => setPetGender(event.target.value)}
                                value={PetGender}
                                disabled={isFormDisabled}
                            >
                                <option value="Male">{translate("Male")}</option>
                                <option value="Female">{translate("Female")}</option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                label={translate("Spayed Or Neutered")}
                                onChange={event => setSpayedOrNeutered(event.target.value)}
                                value={SpayedOrNeutered}
                                disabled={isFormDisabled}
                            >
                                <option value="true">{translate("True")}</option>
                                <option value="false">{translate("False")}</option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <Button type="button" variant="secondary" sizeVariant="small" onClick={cancelAndBack}>
                                {genticKitsActivateInReview ? translate("Back") : translate("Cancel")}
                            </Button>
                            <Button type="submit" variant="primary" sizeVariant="small">
                                {genticKitsActivateInReview ? translate("Active") : translate("Review")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </form>

                {errorMessage && (
                    <Notify type="danger" {...styles.errorMessage}>
                        {errorMessage}
                    </Notify>
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ActivateGeneticKit)),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Activate Genetic Kit",
    },
};

export default widgetModule;
